/**
 * MontaTech v2 - Geometry Utilities
 * Funções de geometria para cálculos e transformações
 */

import type { Point, BoundingBox, RCurve, RFigure } from '../types';

/**
 * Calcula bounding box de um array de pontos
 */
export function calculateBoundingBox(points: Point[]): BoundingBox {
  if (points.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  points.forEach(p => {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  });

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY
  };
}

/**
 * Calcula bounding box de múltiplos arrays de pontos
 */
export function calculateCombinedBoundingBox(paths: Point[][]): BoundingBox {
  const allPoints: Point[] = [];
  paths.forEach(path => allPoints.push(...path));
  return calculateBoundingBox(allPoints);
}

/**
 * Verifica se um ponto está dentro de um retângulo
 */
export function pointInRect(
  px: number, py: number,
  rx: number, ry: number,
  rw: number, rh: number
): boolean {
  return px >= rx && px <= rx + rw && py >= ry && py <= ry + rh;
}

/**
 * Verifica se dois retângulos se intersectam
 */
export function rectsIntersect(
  r1x: number, r1y: number, r1w: number, r1h: number,
  r2x: number, r2y: number, r2w: number, r2h: number
): boolean {
  return !(
    r1x + r1w < r2x ||
    r2x + r2w < r1x ||
    r1y + r1h < r2y ||
    r2y + r2h < r1y
  );
}

/**
 * Calcula distância entre dois pontos
 */
export function distance(p1: Point, p2: Point): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Converte graus para radianos
 */
export function degreesToRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Converte radianos para graus
 */
export function radiansToDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}

/**
 * Converte milímetros para microns
 */
export function mmToMicrons(mm: number): number {
  return mm * 1000;
}

/**
 * Converte microns para milímetros
 */
export function micronsToMm(microns: number): number {
  return microns / 1000;
}

/**
 * Converte polegadas para milímetros
 */
export function inchesToMm(inches: number): number {
  return inches * 25.4;
}

/**
 * Converte milímetros para polegadas
 */
export function mmToInches(mm: number): number {
  return mm / 25.4;
}

/**
 * Converte microns para polegadas
 */
export function micronsToInches(microns: number): number {
  return microns / 25.4 / 1000;
}

/**
 * Calcula área de uma curva (bounding box)
 */
export function calculateCurveArea(curve: RCurve): number {
  return curve.width * curve.height;
}

/**
 * Ordena curvas por área (maior para menor)
 */
export function sortCurvesByArea(curves: RCurve[]): number[] {
  const indices = curves.map((_, i) => i + 1); // 1-based

  indices.sort((a, b) => {
    const areaA = calculateCurveArea(curves[a - 1]);
    const areaB = calculateCurveArea(curves[b - 1]);
    return areaB - areaA; // Maior para menor
  });

  return indices;
}

/**
 * Transforma coordenadas de canvas para coordenadas do mundo
 */
export function canvasToWorld(
  canvasX: number,
  canvasY: number,
  panX: number,
  panY: number,
  zoom: number
): Point {
  return {
    x: (canvasX - panX) / zoom,
    y: (canvasY - panY) / zoom
  };
}

/**
 * Transforma coordenadas do mundo para coordenadas de canvas
 */
export function worldToCanvas(
  worldX: number,
  worldY: number,
  panX: number,
  panY: number,
  zoom: number
): Point {
  return {
    x: worldX * zoom + panX,
    y: worldY * zoom + panY
  };
}

/**
 * Cria grade espacial para hit-testing eficiente
 */
export function createSpatialGrid<T>(
  items: T[],
  getBoundingBox: (item: T) => BoundingBox,
  cellSize: number = 100
): Map<string, T[]> {
  const grid = new Map<string, T[]>();

  items.forEach(item => {
    const bbox = getBoundingBox(item);
    
    const minCellX = Math.floor(bbox.minX / cellSize);
    const maxCellX = Math.floor(bbox.maxX / cellSize);
    const minCellY = Math.floor(bbox.minY / cellSize);
    const maxCellY = Math.floor(bbox.maxY / cellSize);

    for (let cx = minCellX; cx <= maxCellX; cx++) {
      for (let cy = minCellY; cy <= maxCellY; cy++) {
        const key = `${cx},${cy}`;
        if (!grid.has(key)) {
          grid.set(key, []);
        }
        grid.get(key)!.push(item);
      }
    }
  });

  return grid;
}

/**
 * Busca itens na grade espacial próximos a um ponto
 */
export function queryGrid<T>(
  grid: Map<string, T[]>,
  x: number,
  y: number,
  cellSize: number = 100
): T[] {
  const cx = Math.floor(x / cellSize);
  const cy = Math.floor(y / cellSize);
  const key = `${cx},${cy}`;
  return grid.get(key) || [];
}

/**
 * Calcula centro de um bounding box
 */
export function getBoundingBoxCenter(bbox: BoundingBox): Point {
  return {
    x: bbox.minX + bbox.width / 2,
    y: bbox.minY + bbox.height / 2
  };
}

/**
 * Expande um bounding box por uma margem
 */
export function expandBoundingBox(bbox: BoundingBox, margin: number): BoundingBox {
  return {
    minX: bbox.minX - margin,
    minY: bbox.minY - margin,
    maxX: bbox.maxX + margin,
    maxY: bbox.maxY + margin,
    width: bbox.width + margin * 2,
    height: bbox.height + margin * 2
  };
}

